import { useState, useEffect } from "react";
import { doc, updateDoc, collection, getDocs } from "firebase/firestore";
import getFirebase from "../../utilities/firebase";
import { createLogger } from "../../utilities/logger";

const log = createLogger("ManageDemoOrganizationsTool");

const COMPONENT_CLASS = "c-manage-demo-orgs-tool";

const ManageDemoOrganizationsTool: React.FC = () => {
    const [nonDemoOrgs, setNonDemoOrgs] = useState<{ id: string; name: string }[]>([]);
    const [demoOrgs, setDemoOrgs] = useState<{ id: string; name: string }[]>([]);
    const [selectedNonDemoOrgId, setSelectedNonDemoOrgId] = useState<string>("");
    const [selectedDemoOrgId, setSelectedDemoOrgId] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<string>("");

    const ALLOWED_PROJECT_ID = "symmio-app-stage";
    const currentProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
    const isAllowed = currentProjectId === ALLOWED_PROJECT_ID;

    useEffect(() => {
        const fetchOrgs = async () => {
            const { db } = getFirebase();
            try {
                const allOrgsSnap = await getDocs(collection(db, "organizations"));
                const nonDemo: { id: string; name: string }[] = [];
                const demo: { id: string; name: string }[] = [];
                allOrgsSnap.docs.forEach(d => {
                    const data = d.data();
                    if (data.isDemo === true) {
                        demo.push({ id: d.id, name: data.name || d.id });
                    } else {
                        nonDemo.push({ id: d.id, name: data.name || d.id });
                    }
                });
                nonDemo.sort((a, b) => a.name.localeCompare(b.name));
                demo.sort((a, b) => a.name.localeCompare(b.name));
                setNonDemoOrgs(nonDemo);
                setDemoOrgs(demo);
                if (nonDemo.length > 0) setSelectedNonDemoOrgId(nonDemo[0].id);
                if (demo.length > 0) setSelectedDemoOrgId(demo[0].id);
            } catch (e) {
                log.error("Error fetching orgs", {
                    exception: e,
                });
            }
        };
        fetchOrgs();
    }, []);

    const makeDemo = async () => {
        if (!selectedNonDemoOrgId) return;
        setLoading(true);
        setResult("");

        if (!isAllowed) {
            setResult(`Blocked! This tool only works on staging (current: ${currentProjectId})`);
            setLoading(false);
            return;
        }

        try {
            const { db } = getFirebase();
            await updateDoc(doc(db, "organizations", selectedNonDemoOrgId), { isDemo: true });
            setResult(`Success! Organization ${selectedNonDemoOrgId} is now a demo org`);

            setNonDemoOrgs(prev => prev.filter(o => o.id !== selectedNonDemoOrgId));
            const org = nonDemoOrgs.find(o => o.id === selectedNonDemoOrgId);
            if (org) setDemoOrgs(prev => [...prev, org]);
        } catch (e: any) {
            setResult(`Error: ${e.message}`);
        }

        setLoading(false);
    };

    const removeDemo = async () => {
        if (!selectedDemoOrgId) return;
        setLoading(true);
        setResult("");

        if (!isAllowed) {
            setResult(`Blocked! This tool only works on staging (current: ${currentProjectId})`);
            setLoading(false);
            return;
        }

        try {
            const { db } = getFirebase();
            await updateDoc(doc(db, "organizations", selectedDemoOrgId), { isDemo: false });
            setResult(`Success! Organization ${selectedDemoOrgId} is no longer a demo org`);

            setDemoOrgs(prev => prev.filter(o => o.id !== selectedDemoOrgId));
            const org = demoOrgs.find(o => o.id === selectedDemoOrgId);
            if (org) setNonDemoOrgs(prev => [...prev, org]);
        } catch (e: any) {
            setResult(`Error: ${e.message}`);
        }

        setLoading(false);
    };

    if (!isAllowed) {
        return (
            <div className={COMPONENT_CLASS} style={{ maxWidth: 800 }}>
                <h3>Manage Demo Organizations</h3>
                <div style={{ padding: 16, backgroundColor: "#f8d7da", borderRadius: 4, color: "#721c24" }}>
                    <strong>Access Denied:</strong> This tool only works on staging (symmio-app-stage).<br />
                    <strong>Current project:</strong> {currentProjectId || "not set"}
                </div>
            </div>
        );
    }

    return (
        <div className={COMPONENT_CLASS} style={{ maxWidth: 800 }}>
            <h3>Manage Demo Organizations</h3>
            <div style={{ marginBottom: 16, padding: 8, backgroundColor: "#d4edda", borderRadius: 4 }}>
                <strong>Current project:</strong> {currentProjectId}
            </div>

            <div style={{ display: "flex", gap: 32, marginBottom: 24 }}>
                <div>
                    <h4>Non-Demo Organizations</h4>
                    <select
                        value={selectedNonDemoOrgId}
                        onChange={(e) => setSelectedNonDemoOrgId(e.target.value)}
                        style={{ padding: 8, width: 300 }}
                    >
                        {nonDemoOrgs.map(org => (
                            <option key={org.id} value={org.id}>{org.name} ({org.id})</option>
                        ))}
                    </select>
                    <button
                        onClick={makeDemo}
                        disabled={loading || !selectedNonDemoOrgId || !isAllowed}
                        style={{ padding: "8px 16px", marginLeft: 8, cursor: loading ? "not-allowed" : "pointer" }}
                    >
                        {loading ? "..." : "Make Demo"}
                    </button>
                </div>

                <div>
                    <h4>Demo Organizations</h4>
                    <select
                        value={selectedDemoOrgId}
                        onChange={(e) => setSelectedDemoOrgId(e.target.value)}
                        style={{ padding: 8, width: 300 }}
                    >
                        {demoOrgs.map(org => (
                            <option key={org.id} value={org.id}>{org.name} ({org.id})</option>
                        ))}
                    </select>
                    <button
                        onClick={removeDemo}
                        disabled={loading || !selectedDemoOrgId || !isAllowed}
                        style={{ padding: "8px 16px", marginLeft: 8, cursor: loading ? "not-allowed" : "pointer" }}
                    >
                        {loading ? "..." : "Remove Demo"}
                    </button>
                </div>
            </div>

            {result && (
                <div style={{ marginTop: 16, padding: 8, backgroundColor: result.startsWith("Error") ? "#f8d7da" : "#d4edda", borderRadius: 4 }}>
                    {result}
                </div>
            )}
        </div>
    );
};

export default ManageDemoOrganizationsTool;